'use client'

import dynamic from 'next/dynamic'
import { useTheme, alpha } from '@mui/material/styles'
import { Box } from '@mui/material'
import { blue, blueGrey, lightBlue, pink, lime, brown, amber } from '@mui/material/colors'

// Types
import type { TextProps } from '@/types/components/atoms/text'

// Componenets
const TransAtom = dynamic(() => import('@/components/atoms/text/trans'))

const TextAtom = (props: TextProps) => {
	// Props
	const { children } = props

	// Variables
	const muiTheme = useTheme()

	return (
		<Box
			sx={{
				p: 0,
				'& img': { maxWidth: '100%' },
				'& pre::-webkit-scrollbar': {
					width: 10,
					backgroundColor: 'transparent'
				},
				' &pre::-webkit-scrollbar-thumb': { backgroundColor: muiTheme.palette.grey[600] },
				'& .ltr': { textAlign: muiTheme.direction === 'rtl' ? 'right' : 'left' },
				'& .rtl': { textAlign: muiTheme.direction === 'rtl' ? 'left' : 'right' },
				'& .editor-paragraph': {
					position: 'relative',
					m: 0,
					mb: 1,
					'&:last-child': { mb: 0 }
				},
				'& .editor-quote': {
					m: 0,
					ml: 2.5,
					pl: 2,
					fontSize: 15,
					color: muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[300] : muiTheme.palette.grey[700],
					borderLeftColor: muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[300] : muiTheme.palette.grey[700],
					borderLeftWidth: 4,
					borderLeftStyle: 'solid'
				},
				'& .editor-heading-h1': {
					m: 0,
					mb: '0.35em',
					fontSize: '6rem',
					fontWeight: 300,
					lineHeight: 1.167,
					letterSpacing: '-0.01562em',
					color: muiTheme.palette.mode == 'dark' ? muiTheme.palette.secondary.light : '#014178'
				},
				'& .editor-heading-h2': {
					m: 0,
					mb: '0.35em',
					fontSize: '3.75rem',
					fontWeight: 300,
					lineHeight: 1.2,
					letterSpacing: '-0.00833em',
					color: muiTheme.palette.mode == 'dark' ? muiTheme.palette.secondary.light : '#014178'
				},
				'& .editor-heading-h3': {
					m: 0,
					mb: '0.35em',
					fontSize: '3rem',
					fontWeight: 400,
					lineHeight: 1.167,
					color: muiTheme.palette.mode == 'dark' ? muiTheme.palette.secondary.light : '#014178'
				},
				'& .editor-heading-h4': {
					m: 0,
					mb: '0.35em',
					fontSize: '2.125rem',
					fontWeight: 400,
					lineHeight: 1.235,
					letterSpacing: '0.00735em',
					color: muiTheme.palette.mode == 'dark' ? muiTheme.palette.secondary.light : '#014178'
				},
				'& .editor-heading-h5': {
					m: 0,
					mb: '0.35em',
					fontSize: '1.5rem',
					fontWeight: 400,
					lineHeight: 1.334,
					color: muiTheme.palette.mode == 'dark' ? muiTheme.palette.secondary.light : '#014178'
				},
				'& .editor-heading-h6': {
					m: 0,
					mb: '0.35em',
					fontSize: '1.25rem',
					fontWeight: 500,
					lineHeight: 1.6,
					letterSpacing: '0.0075em',
					color: muiTheme.palette.mode == 'dark' ? muiTheme.palette.secondary.light : '#014178'
				},
				'& .editor-nested-listitem': { listStyleType: 'none' },
				'& .editor-list-ol': {
					m: 0,
					ml: 2,
					p: 0
				},
				'& .editor-list-ul': {
					m: 0,
					ml: 2,
					p: 0
				},
				'& .editor-listitem': {
					my: 1,
					mx: 4
				},
				'& .editor-image': {
					cursor: 'default',
					userSelect: 'none',
					display: 'inline-block',
					position: 'relative'
				},
				'& .editor-link': {
					color: blue[500],
					textDecoration: 'none'
				},
				'& .editor-text-bold': {
					fontWeight: 'bold',
					color: muiTheme.palette.mode == 'dark' ? muiTheme.palette.secondary.light : '#014178'
				},
				'& .editor-text-italic': { fontStyle: 'italic' },
				'& .editor-text-overflowed': {},
				'& .editor-text-hashtag': {},
				'& .editor-text-underline': { textDecoration: 'underline' },
				'& .editor-text-strikethrough': { textDecoration: 'line-through' },
				'& .editor-text-underlineStrikethrough': { textDecoration: 'underline line-through' },
				'& .editor-text-code': {
					py: 0.25,
					px: 1,
					fontFamily: 'Menlo, Consolas, Monaco, monospace',
					fontSize: '94%',
					backgroundColor: blueGrey[50]
				},
				'& .editor-table': {
					tableLayout: 'fixed',
					width: 1,
					maxWidth: 1,
					my: 1,
					mx: 0,
					border: '1px solid',
					borderCollapse: 'collapse',
					borderSpacing: 0,
					overflow: 'hidden'
				},
				'& .editor-table-row': {
					display: 'table-row',
					verticalAlign: 'top'
				},
				'& .editor-table-cell': {
					minWidth: 75,
					p: 1,
					position: 'relative',
					verticalAlign: 'top',
					border: '1px solid',
					borderColor: 'grey.300'
				},
				'& .editor-table-cell-header': {
					cursor: 'pointer',
					userSelect: 'none',
					fontWeight: 'bold',
					textAlign: 'left',
					color: 'grey.700',
					bgcolor: 'grey.100',
					'&::after': {
						display: 'none',
						content: '""',
						width: 0,
						height: 0,
						position: 'absolute',
						top: '50%',
						right: 10,
						borderLeft: '5px solid transparent',
						borderRight: '5px solid transparent',
						borderTop: '5px solid',
						borderTopColor: 'grey.700',
						transform: 'translateY(-50%)'
					},
					'&.selected::after': { display: 'block' },
					'&.order-desc::after': { transform: 'translateY(-50%) rotate(180deg)' }
				},
				'& .editor-code': {
					display: 'block',
					position: 'relative',
					my: 1,
					mx: 0,
					p: 1,
					pl: 6.5,
					fontFamily: 'Menlo, Consolas, Monaco, monospace',
					fontSize: 13,
					lineHeight: 1.53,
					backgroundColor: muiTheme.palette.grey[50],
					overflowX: 'auto',
					tabSize: 2,
					'&::before': {
						content: 'attr(data-gutter)',
						minWidth: 25,
						height: '100%',
						position: 'absolute',
						left: 0,
						top: 0,
						p: 1,
						whiteSpace: 'pre-wrap',
						textAlign: 'right',
						color: muiTheme.palette.grey[500],
						backgroundColor: muiTheme.palette.grey[100],
						borderRight: `1px solid ${muiTheme.palette.grey[200]}`
					},
					'&::after': {
						content: 'attr(data-highlight-language)',
						height: '100%',
						position: 'absolute',
						top: 0,
						right: 3,
						p: 0.375,
						fontSize: 10,
						textTransform: 'uppercase',
						color: alpha(muiTheme.palette.common.black, 0.5)
					}
				},
				'& .editor-tokenAttr': { color: lightBlue[800] },
				'& .editor-tokenProperty': { color: pink[900] },
				'& .editor-tokenSelector': { color: lime[700] },
				'& .editor-tokenComment': { color: blueGrey[800] },
				'& .editor-tokenFunction': { color: pink[400] },
				'& .editor-tokenOperator': { color: brown[500] },
				'& .editor-tokenVariable': { color: amber[700] },
				'& .editor-tokenPunctuation': { color: muiTheme.palette.grey[500] },
				'& .editor-video-embed': {
					position: 'relative',
					paddingBottom: '56.25%',
					height: 0,
					overflow: 'hidden',
					maxWidth: '100%',
					'& iframe': {
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%'
					}
				}
			}}
		>
			<TransAtom>{children}</TransAtom>
		</Box>
	)
}

export default TextAtom
